// CSV export for the team tables (AHA — Export button next to "Upgrade
// team plan"). Builds the exact column structure the user sees on screen
// for the active tab, then streams the file to the browser as a download.
//
// Members tab  → exports the members currently in view.
// Pending tab  → exports the invitations currently in view.
//
// Label text mirrors what the table cells render (role label, plan name,
// "Free"/"Auto" placeholders) so the CSV reads the same as the screen.

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { TFunction } from "i18next";
import type { Invitation, TeamMember } from "./types";
import { getTeamRoleI18nKey } from "./teamRole";
import { getPlanDisplayName } from "./planNames";
import { hasPersonalPlan } from "./memberPlanRules";
import { memberDisplayName } from "./memberDisplay";

dayjs.extend(relativeTime);

// react-i18next's `t`. Used to mirror the cell text the tables render.
type Translate = TFunction;

export interface CsvTable {
  headers: string[];
  rows: string[][];
}

function escapeCsvCell(value: string): string {
  // OWASP CSV-injection guard: a cell beginning with one of these is
  // evaluated as a formula by Excel / Sheets / LibreOffice. Member names and
  // emails are user-controllable, so neutralize the leading char with a
  // single quote. This is NOT part of RFC 4180 (it deliberately mutates the
  // value); it has to run before the structural quoting below.
  let cell = value;
  if (/^[=+\-@\t\r]/.test(cell)) {
    cell = `'${cell}`;
  }
  // RFC 4180 escaping: wrap in double quotes and double any embedded quote
  // whenever the value contains a comma, quote, or newline. Plain values pass
  // through untouched.
  if (/[",\r\n]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

export function toCsv({ headers, rows }: CsvTable): string {
  const lines = [headers, ...rows].map((cells) =>
    cells.map((c) => escapeCsvCell(c ?? "")).join(","),
  );
  // CRLF line endings — RFC 4180 and the row separator Excel expects.
  return lines.join("\r\n");
}

// Stream the generated text to the browser as a file download. A leading
// UTF-8 BOM makes Excel honour non-ASCII names; the object URL is revoked on
// the next tick so we don't leak the blob.
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Defer revoke so the click-initiated download has the URL resolved.
  // Some browsers (notably iOS Safari) abort the download if the object URL
  // is revoked on the very next tick, so give it a short grace window.
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

// Mirror of MemberLicenseSelection's rendered text for the License column.
function memberLicenseLabel(
  member: TeamMember,
  teamPlan: string | null | undefined,
  t: Translate,
): string {
  if (hasPersonalPlan(member, teamPlan)) {
    return getPlanDisplayName(member.plan);
  }
  if (member.license) {
    return getPlanDisplayName(teamPlan) || t("team.license.licensed");
  }
  return t("team.license.unlicensed");
}

export interface BuildMembersCsvOptions {
  /** Same gate as the table: adds Last activity + Joined columns. */
  showDetailedColumns: boolean;
  /** Same gate as the table: adds the Licence column on paid teams. */
  isPaidTeamPlan: boolean;
  teamPlan: string | null | undefined;
  t: Translate;
}

// Columns mirror MembersTable. Email is split out of the Name cell (the table
// renders the address under the name) so each visible field is its own
// parseable column. The empty actions column is omitted — it carries no data.
export function buildMembersCsv(
  members: TeamMember[],
  { showDetailedColumns, isPaidTeamPlan, teamPlan, t }: BuildMembersCsvOptions,
): CsvTable {
  const fallback = t("team.role.member");

  const headers = [
    t("team.column.name"),
    t("team.column.email"),
    ...(showDetailedColumns
      ? [t("team.column.lastActivity"), t("team.column.joined")]
      : []),
    t("team.column.role"),
    ...(isPaidTeamPlan ? [t("team.column.license")] : []),
  ];

  const rows = members.map((m) => [
    memberDisplayName(m, fallback),
    m.email ?? "",
    ...(showDetailedColumns
      ? [
          m.lastActivity ? dayjs(m.lastActivity).fromNow() : "-",
          m.accountData?.joinedAt
            ? dayjs(m.accountData.joinedAt).format("D MMM YYYY")
            : "-",
        ]
      : []),
    t(getTeamRoleI18nKey(m.accountRole)),
    ...(isPaidTeamPlan ? [memberLicenseLabel(m, teamPlan, t)] : []),
  ]);

  return { headers, rows };
}

export interface BuildInvitationsCsvOptions {
  /** Same gate as the table: adds the Licence column on paid teams. */
  isPaidTeamPlan: boolean;
  t: Translate;
}

// Columns mirror InvitationsTable. The trailing withdraw-button column is
// omitted — it carries no data.
export function buildInvitationsCsv(
  invitations: Invitation[],
  { isPaidTeamPlan, t }: BuildInvitationsCsvOptions,
): CsvTable {
  const headers = [
    t("team.column.email"),
    t("team.column.invitationLink"),
    t("team.column.role"),
    ...(isPaidTeamPlan ? [t("team.column.license")] : []),
    t("team.column.status"),
  ];

  const rows = invitations.map((inv) => [
    inv.invitedEmail,
    inv.link ?? "",
    t(getTeamRoleI18nKey(inv.accountRole)),
    ...(isPaidTeamPlan
      ? [inv.assignLicenseOnAccept ? t("team.license.willAssignOnAccept") : "—"]
      : []),
    t(`team.status.${inv.status}`, inv.status),
  ]);

  return { headers, rows };
}
